import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, AlertCircle } from 'lucide-react';
import {
  DataGrid,
  FilterBar,
  Button,
  MetricCard,
  METRIC_READ_CAPTIONS,
  Skeleton,
} from '@aaimplatform/ui';
import type {
  FilterDefinition,
  ActiveFilter,
  ColumnDef,
} from '@aaimplatform/ui';
import { usePledges } from '../hooks/usePledges';
import type { PledgeSummary, PledgeStatus } from '../hooks/usePledges';
import {
  formatCount,
  formatDateTime,
  formatMoney,
  formatMoneyCompact,
} from '../utils/display-format';
import {
  ChaTierBadge,
  PLEDGE_STATUS_FILTER_OPTIONS,
  PledgeStatusBadge,
} from '../utils/status-labels';
import { RouterTableLink } from '../components/RouterTableLink';

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

function useColumns(): ColumnDef<PledgeSummary, unknown>[] {
  const navigate = useNavigate();
  return useMemo<ColumnDef<PledgeSummary, unknown>[]>(
    () => [
      {
        accessorKey: 'memberEmail',
        header: 'Member',
        // The kit TableLink: a real link in the secondary color. The red
        // <button> set the member name in the CTA color beside the red
        // Review button.
        cell: ({ row }) => (
          <div>
            <RouterTableLink to={`/pledges/${row.original.id}`} className="text-sm leading-normal">
              {row.original.memberName ?? row.original.memberEmail}
            </RouterTableLink>
            {row.original.memberName && (
              <div className="text-xs font-sans text-tertiary leading-normal">
                {row.original.memberEmail}
              </div>
            )}
            {row.original.fiName && (
              <div className="text-xs font-sans text-tertiary leading-normal">
                {row.original.fiName}
              </div>
            )}
          </div>
        ),
        size: 240,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ getValue }) => <PledgeStatusBadge status={getValue() as string} />,
        size: 130,
      },
      {
        accessorKey: 'totalCollateralValue',
        header: 'Collateral Value',
        meta: { align: 'right' },
        cell: ({ getValue }) => (
          <span className="text-sm font-mono text-primary tabular-nums leading-normal">
            {formatMoney(getValue() as number)}
          </span>
        ),
        size: 160,
      },
      {
        accessorKey: 'totalPledgeableAmount',
        header: 'Pledgeable Amount',
        meta: { align: 'right' },
        cell: ({ getValue }) => (
          <span className="text-sm font-mono text-primary tabular-nums leading-normal">
            {formatMoney(getValue() as number)}
          </span>
        ),
        size: 170,
      },
      {
        accessorKey: 'accountCount',
        header: 'Accounts',
        meta: { align: 'right' },
        cell: ({ getValue }) => (
          <span className="text-sm font-mono text-primary tabular-nums leading-normal">
            {formatCount(getValue() as number)}
          </span>
        ),
        size: 90,
      },
      {
        accessorKey: 'currentChaTier',
        header: 'CHA Tier',
        cell: ({ getValue }) => <ChaTierBadge tier={getValue() as string | undefined} />,
        size: 100,
      },
      {
        // Date and time with the zone named, as on the FI Tasks list and on
        // Pledge Detail. The list printed the date alone.
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ getValue }) => (
          <span className="text-sm font-sans text-secondary leading-normal">
            {formatDateTime(getValue() as string)}
          </span>
        ),
        size: 170,
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <Button
            variant="secondary"
            size="sm"
            icon={FileText}
            onClick={() => {
              navigate(`/pledges/${row.original.id}`);
            }}
          >
            Review
          </Button>
        ),
        size: 110,
      },
    ],
    [navigate],
  );
}

// ---------------------------------------------------------------------------
// Filter definitions
// ---------------------------------------------------------------------------

/**
 * Status filter options for the cross-tenant pledge list, DERIVED from the
 * generated `PledgeStatus` union via the exhaustive keyed label record in
 * utils/status-labels.tsx, the same record the status badge reads. A status
 * added to schema-registry/platform-admin.openapi.yaml makes the record
 * non-exhaustive and fails tsc, so neither the filter nor the badge can fall
 * behind the enum. The derivation is PLEDGE_STATUS_FILTER_OPTIONS in that
 * module, which FI Tasks reads as well.
 *
 * Drift history: the prior hand-listed options omitted DECLINED and RETURNED, so
 * an Aaim operator could not filter for declined or returned pledges from the one
 * cross-tenant list that is supposed to show every pledge.
 *
 * Pinned by src/pages/__tests__/Pledges.test.tsx.
 */
export const STATUS_FILTER_OPTIONS = PLEDGE_STATUS_FILTER_OPTIONS;

const FILTER_DEFINITIONS: FilterDefinition[] = [
  {
    id: 'status',
    label: 'Status',
    type: 'select',
    options: [...STATUS_FILTER_OPTIONS],
  },
  {
    id: 'member',
    label: 'Member',
    type: 'text',
    placeholder: 'Search by member name or email...',
  },
];

// ---------------------------------------------------------------------------
// PledgesPage
// ---------------------------------------------------------------------------

/**
 * Pledges page -- cross-tenant list of pledge proposals.
 *
 * Fetches from GET /api/admin/pledges (platform-admin-api, port 9302).
 * Supports status filtering and navigates to /pledges/:pledgeId for detail.
 */
export function PledgesPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const columns = useColumns();

  // Pull active status filter for server-side filtering
  const statusFilter = activeFilters.find((f) => f.filterId === 'status')
    ?.value as PledgeStatus | undefined;

  const { data: response, isLoading, error } = usePledges(
    statusFilter ? { status: statusFilter } : undefined,
  );

  const pledges = response?.pledges ?? [];

  // Client-side search filter (member name / email)
  const filteredPledges = useMemo(() => {
    if (!searchQuery.trim()) return pledges;
    const query = searchQuery.toLowerCase();
    return pledges.filter(
      (p) =>
        p.memberEmail.toLowerCase().includes(query) ||
        (p.memberName ?? '').toLowerCase().includes(query) ||
        (p.fiName ?? '').toLowerCase().includes(query),
    );
  }, [pledges, searchQuery]);

  // ---------------------------------------------------------------------------
  // Summary metrics
  // ---------------------------------------------------------------------------

  const pendingCount = useMemo(
    () => filteredPledges.filter((p) => p.status === 'PENDING').length,
    [filteredPledges],
  );

  const totalCollateral = useMemo(
    () =>
      filteredPledges.reduce((sum, p) => sum + p.totalCollateralValue, 0),
    [filteredPledges],
  );

  const totalPledgeable = useMemo(
    () =>
      filteredPledges.reduce(
        (sum, p) => sum + p.totalPledgeableAmount,
        0,
      ),
    [filteredPledges],
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div>
      {/* Error state */}
      {error && (
        <div className="bg-error/5 border border-error/20 rounded-sm p-4 mb-6 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-error flex-shrink-0" />
          <div className="text-sm font-sans text-error">
            Failed to load pledges: {error.message}
          </div>
        </div>
      )}

      {/* Summary metrics, two per row on a phone as on the Dashboard */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-surface p-6 rounded-sm shadow-sm">
              <Skeleton className="h-10 w-12 mb-3" />
              <Skeleton className="h-3 w-24 mb-2" />
              <Skeleton className="h-3 w-16" />
            </div>
          ))
        ) : error ? (
          // The list read failed. The empty list's zeros ("None pending",
          // "$0") would present an unread queue as a measured all-clear.
          <>
            <MetricCard label="Total Pledges" value="--" caption={METRIC_READ_CAPTIONS.failed} />
            <MetricCard label="Pending Review" value="--" caption={METRIC_READ_CAPTIONS.failed} />
            <MetricCard label="Total Collateral" value="--" caption={METRIC_READ_CAPTIONS.failed} />
            <MetricCard label="Total Pledgeable" value="--" caption={METRIC_READ_CAPTIONS.failed} />
          </>
        ) : (
          <>
            {/* Prose goes through description; change is reserved for a
                signed movement and renders monospace in the change color. */}
            <MetricCard
              label="Total Pledges"
              value={formatCount(filteredPledges.length)}
              description={`${formatCount(response?.total ?? filteredPledges.length)} platform-wide`}
            />
            <MetricCard
              label="Pending Review"
              value={formatCount(pendingCount)}
              description={pendingCount > 0 ? 'Requires attention' : 'None pending'}
            />
            <MetricCard
              label="Total Collateral"
              value={formatMoneyCompact(totalCollateral)}
              description="Across listed pledges"
            />
            <MetricCard
              label="Total Pledgeable"
              value={formatMoneyCompact(totalPledgeable)}
              description="After haircuts and LTV"
            />
          </>
        )}
      </div>

      {/* Filter bar */}
      <FilterBar
        filters={FILTER_DEFINITIONS}
        activeFilters={activeFilters}
        onFiltersChange={setActiveFilters}
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search by member name, email, or institution..."
        className="rounded-t-sm"
      />

      {/* Data grid */}
      {isLoading ? (
        <div className="bg-surface rounded-sm shadow-sm p-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full mb-2" />
          ))}
        </div>
      ) : (
        <DataGrid<PledgeSummary>
          data={filteredPledges}
          columns={columns}
          enablePagination
          defaultPageSize={20}
          enableColumnResize
          hoverable
          striped
          getRowId={(row) => row.id}
          className="w-full rounded-b-sm shadow-sm"
        />
      )}
    </div>
  );
}
